import { Eyebrow, LinkButton } from '@/components/ui';
import { CheckIcon, CloseIcon, SparkIcon } from '@/lib/icons';

const FREE = [
  'Upload any programme (CSV, JSON or template)',
  'Log sets, reps, weight, distance, time and RPE',
  'Every track: strength, bodybuilding, conditioning, performance, wellness',
  'Unlimited personal records, streaks and history',
  'The programme builder, goals and the block summary',
  'Generate your plan on the free trial',
  'Web, iOS, Android and watchOS — one synced log',
  'Offline logging with automatic sync',
  'CSV / JSON export of everything you have logged',
];

const PREMIUM = [
  'Everything in Free',
  'A specialist AI coach for your track: strength, bodybuilding, conditioning, performance or wellness',
  'Weekly review with next week’s sets, reps, loads and intervals prescribed',
  'Progressive-overload and fatigue detection with the evidence attached',
  'Deload triggers with automatic volume and intensity reduction',
  'On-demand check-ins: “should I train through this?”',
  'Injury-aware substitutions and blocked movements',
];

const NOT_IN_FREE = ['The specialist AI coach', 'Weekly review and prescriptions'];

export function TierTable() {
  return (
    <section id="tiers" className="border-b border-hairline bg-page py-20">
      <div className="mx-auto max-w-[1200px] 2xl:max-w-[1440px] min-[1920px]:max-w-[1720px] px-4 sm:px-6">
        <div className="mb-10 flex flex-col gap-4">
          <Eyebrow>Two tiers, no dark patterns</Eyebrow>
          <h2 className="font-display text-h2 font-black uppercase leading-tight tracking-[-0.01em] text-ink md:text-display-2">
            Logging is free. AI coaching is Premium.
          </h2>
          <p className="max-w-[70ch] text-body-lg text-ink-muted">
            The tracker is a tool, so it is free forever — no card, no feature held hostage. Generate your plan on
            the free trial; the AI coach is the service, at £3.99 a month, reading your log and rewriting what comes
            next.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <div className="flex flex-col rounded-card border border-hairline bg-surface p-7 shadow-card">
            <h3 className="font-display text-[22px] font-black uppercase tracking-[-0.01em] text-ink">Free</h3>
            <p className="mt-1 text-[13px] text-ink-muted">Every track, on every screen you own.</p>
            <p className="l2f-tabular mt-5 font-mono text-4xl font-bold text-ink">
              £0<span className="ml-2 text-[13px] font-semibold text-ink-muted">free forever</span>
            </p>
            <ul className="mt-6 flex flex-1 flex-col gap-3">
              {FREE.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-[13px] leading-snug text-ink">
                  <CheckIcon className="mt-[2px] shrink-0 text-sm text-ink" />
                  {item}
                </li>
              ))}
              {NOT_IN_FREE.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-[13px] leading-snug text-ink-muted">
                  <CloseIcon className="mt-[2px] shrink-0 text-sm text-ink-muted" />
                  {item}
                </li>
              ))}
            </ul>
            <LinkButton href="#pricing" variant="outline" size="md" className="mt-7">
              Start free
            </LinkButton>
          </div>

          <div className="relative flex flex-col overflow-hidden rounded-card border border-hairline bg-surface p-7 shadow-modal">
            <span aria-hidden="true" className="absolute inset-x-0 top-0 h-[3px] bg-accent" />
            <div className="flex items-center justify-between">
              <h3 className="font-display text-[22px] font-black uppercase tracking-[-0.01em] text-ink">Premium</h3>
              <span className="inline-flex items-center gap-1.5 rounded-pill border border-accent bg-accent/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-accent">
                <SparkIcon className="text-xs" /> AI Performance Coach
              </span>
            </div>
            <p className="mt-1 text-[13px] text-ink-muted">A specialist AI coach that reads your log and rewrites what comes next.</p>
            <p className="l2f-tabular mt-5 font-mono text-4xl font-bold text-ink">
              £3.99<span className="ml-2 text-[13px] font-semibold text-ink-muted">/ month</span>
            </p>
            <ul className="mt-6 flex flex-1 flex-col gap-3">
              {PREMIUM.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-[13px] leading-snug text-ink">
                  <CheckIcon className="mt-[2px] shrink-0 text-sm text-accent" />
                  {item}
                </li>
              ))}
            </ul>
            <LinkButton href="#pricing" size="md" className="mt-7">
              Start 14 days free
            </LinkButton>
          </div>
        </div>
      </div>
    </section>
  );
}
